import { motion } from "framer-motion";
import {
  ArrowRight,
  FileSignature,
  MailCheck,
  ShieldCheck
} from "lucide-react";
import { Link } from "react-router-dom";

const featureCards = [
  {
    title: "Simple account setup",
    description:
      "Create an account, verify your email, and come back once approval is complete.",
    icon: ShieldCheck
  },
  {
    title: "Easy agreement form",
    description:
      "Fill in your details step by step, review the agreement, and sign before submitting.",
    icon: FileSignature
  },
  {
    title: "Clear review process",
    description:
      "Your submitted agreement is reviewed by the admin team and updated in one place.",
    icon: MailCheck
  }
];

const workflowSteps = [
  "Create your account.",
  "Verify your email with the one-time code.",
  "Wait for admin approval to unlock sign in.",
  "Fill in your agreement details and add your signature.",
  "Submit your agreement for review.",
  "Track the review result from your candidate workspace."
];

const faqs = [
  {
    question: "Do candidates need an account before they can access the portal?",
    answer:
      "Yes. Candidates first create an account, verify their email, and then wait for approval before the full workspace is available."
  },
  {
    question: "When is the digital signature captured?",
    answer:
      "The signature is collected during the agreement form and is required before submission."
  },
  {
    question: "When does the review happen?",
    answer:
      "Review happens after the signed agreement is submitted to the admin dashboard."
  }
];

export function LandingPage() {
  return (
    <div>
      <section className="mx-auto max-w-[1200px] px-6 py-16 md:py-24">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-[900px] space-y-8 text-center"
          initial={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="space-y-5">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-royal/70">
              Gupio Legal Portal
            </p>
            <h1 className="mx-auto max-w-4xl text-5xl leading-[1.02] text-ink md:text-7xl">
              A simple way to complete and sign your internship agreement.
            </h1>
            <p className="mx-auto max-w-3xl text-lg leading-8 text-slate">
              Create your account, verify your email, fill in your details, and submit your
              agreement in one clear flow.
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link className="btn-primary" to="/apply">
              Get Started
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a className="btn-secondary" href="#workflow">
              See the process
            </a>
          </div>
        </motion.div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-8" id="program-overview">
        <div className="grid gap-5 lg:grid-cols-3">
          {featureCards.map(({ title, description, icon: Icon }, index) => (
            <motion.article
              key={title}
              className="surface p-6 md:p-7"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: index * 0.08, duration: 0.4, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.25 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-gradient-to-br from-navy to-royal text-paper shadow-glow">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-6 text-2xl font-semibold tracking-tight text-ink">{title}</h3>
              <p className="mt-4 text-base leading-7 text-slate">{description}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-16 md:py-24" id="workflow">
        <div className="grid gap-10 xl:grid-cols-[0.82fr_1.18fr]">
          <div className="space-y-5">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-royal/70">
              Portal Process
            </p>
            <h2 className="text-4xl leading-tight text-ink md:text-5xl">
              A clear step-by-step flow from sign up to review.
            </h2>
            <p className="max-w-xl text-lg leading-8 text-slate">
              Each step is designed to be easy to follow, even for first-time users.
            </p>
          </div>

          <div className="space-y-4">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={step}
                className="surface flex gap-5 p-5 md:p-6"
                initial={{ opacity: 0, x: 18 }}
                transition={{ delay: index * 0.07, duration: 0.38, ease: "easeOut" }}
                viewport={{ once: true, amount: 0.25 }}
                whileInView={{ opacity: 1, x: 0 }}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cloud text-lg font-semibold text-royal">
                  {index + 1}
                </div>
                <p className="pt-2 text-base leading-7 text-slate">{step}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-20" id="faq">
        <div className="surface p-7 md:p-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-royal/70">FAQ</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-ink">Common questions</h2>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {faqs.map((item) => (
              <div key={item.question} className="rounded-[24px] border border-line/70 bg-cloud/68 p-5">
                <h3 className="text-lg font-semibold tracking-tight text-ink">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-slate">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
